import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, CalendarClock, Loader2, TrendingDown, Users } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';

const ST_TONE = {
  active: 'bg-sky-100 text-sky-700',
  closed: 'bg-muted text-muted-foreground',
  expired: 'bg-rose-100 text-rose-700',
};

function Kpi({ label, value, color, testid }) {
  return (
    <Card className="p-4 shadow-card" data-testid={testid}>
      <p className="text-2xl font-bold tabular-nums leading-tight" style={color ? { color } : undefined}>{value ?? '—'}</p>
      <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</p>
    </Card>
  );
}

// Founder UAT iter 2 — HM executive supervision: platform-wide Climara KPIs,
// widgets (lowest participation / expiring / no responses / advisor workload)
// and status + advisor filters. Supervise the business, not one campaign.
export default function AdminClimaraSupervision() {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [advisorFilter, setAdvisorFilter] = useState('all');

  useEffect(() => {
    adminApi.climaraSupervision()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => (data?.campaigns || []).filter((r) => {
    if (statusFilter !== 'all' && r.derived_status !== statusFilter) return false;
    if (advisorFilter !== 'all' && (r.advisor_id || '__none__') !== advisorFilter) return false;
    return true;
  }), [data, statusFilter, advisorFilter]);

  const widgets = useMemo(() => {
    const all = data?.campaigns || [];
    const active = all.filter((r) => r.status === 'active');
    const lowest = [...active].filter((r) => r.invited > 0).sort((a, b) => a.rate_pct - b.rate_pct).slice(0, 5);
    const expiring = active.filter((r) => r.days_left != null && r.days_left >= 0 && r.days_left <= 7)
      .sort((a, b) => a.days_left - b.days_left);
    const noResponses = active.filter((r) => r.completed === 0);
    const byAdvisor = {};
    all.forEach((r) => {
      const key = r.advisor_name || (isES ? 'Sin asesor asignado' : 'No advisor assigned');
      byAdvisor[key] = byAdvisor[key] || { advisor: key, companies: new Set(), active: 0, overdue: 0 };
      byAdvisor[key].companies.add(r.company_id);
      if (r.status === 'active') byAdvisor[key].active += 1;
      if (r.derived_status === 'expired') byAdvisor[key].overdue += 1;
    });
    const workload = Object.values(byAdvisor)
      .map((w) => ({ ...w, companies: w.companies.size }))
      .sort((a, b) => b.active - a.active);
    return { lowest, expiring, noResponses, workload };
  }, [data, isES]);

  if (loading) {
    return (
      <div className="flex items-center text-sm text-muted-foreground py-8" data-testid="climara-supervision-loading">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />{isES ? 'Cargando supervisión…' : 'Loading supervision…'}
      </div>
    );
  }
  if (!data) return null;
  const k = data.kpis || {};
  const stLabel = (s) => ({
    active: isES ? 'Activa' : 'Active',
    closed: isES ? 'Cerrada' : 'Closed',
    expired: isES ? 'Vencida' : 'Expired',
  }[s] || s);

  return (
    <div className="space-y-6" data-testid="climara-supervision">
      {/* Executive KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="climara-supervision-kpis">
        <Kpi label={isES ? 'Campañas activas' : 'Active campaigns'} value={k.active_campaigns} color="#0284c7" testid="clsup-kpi-active" />
        <Kpi label={isES ? 'Completadas' : 'Completed'} value={k.closed_campaigns} testid="clsup-kpi-closed" />
        <Kpi label={isES ? 'Participación media (activas)' : 'Avg participation (active)'} value={k.avg_participation != null ? `${k.avg_participation}%` : '—'}
             color={(k.avg_participation || 0) >= 70 ? '#16a34a' : '#d97706'} testid="clsup-kpi-participation" />
        <Kpi label={isES ? 'Finalización media (cerradas)' : 'Avg completion (closed)'} value={k.avg_completion_closed != null ? `${k.avg_completion_closed}%` : '—'} testid="clsup-kpi-completion" />
        <Kpi label={isES ? 'Empresas con campaña activa' : 'Companies with active'} value={k.companies_with_active} testid="clsup-kpi-companies" />
        <Kpi label={isES ? 'Pendientes de aprobación' : 'Pending approval'} value={k.pending_approvals}
             color={k.pending_approvals ? '#d97706' : undefined} testid="clsup-kpi-pending" />
        <Kpi label={isES ? 'Empresas en espera' : 'Companies awaiting'} value={k.companies_awaiting_approval} testid="clsup-kpi-awaiting" />
        <Kpi label={isES ? 'Cerca del límite (7 días)' : 'Nearing deadline (7d)'} value={k.nearing_deadline}
             color={k.nearing_deadline ? '#d97706' : undefined} testid="clsup-kpi-nearing" />
        <Kpi label={isES ? 'Sin respuestas aún' : 'Awaiting responses'} value={k.awaiting_responses}
             color={k.awaiting_responses ? '#d97706' : undefined} testid="clsup-kpi-awaitingresp" />
        <Kpi label={isES ? 'Vencidas' : 'Overdue'} value={k.overdue_campaigns}
             color={k.overdue_campaigns ? '#dc2626' : undefined} testid="clsup-kpi-overdue" />
      </div>

      {/* Supervisory widgets */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5 shadow-card" data-testid="clsup-widget-lowest">
          <h4 className="text-sm font-bold flex items-center gap-2 mb-3">
            <TrendingDown className="h-4 w-4 text-rose-600" />
            {isES ? 'Menor participación (campañas activas)' : 'Lowest participation (active campaigns)'}
          </h4>
          {widgets.lowest.length === 0 ? (
            <p className="text-xs text-muted-foreground">{isES ? 'Sin campañas activas con invitados.' : 'No active campaigns with invitees.'}</p>
          ) : widgets.lowest.map((r) => (
            <div key={r.id} className="flex items-center justify-between text-xs py-1.5 border-b last:border-0">
              <span className="truncate mr-2">{r.company_name} · {r.name}</span>
              <span className="font-bold tabular-nums shrink-0" style={{ color: r.rate_pct < 50 ? '#dc2626' : '#d97706' }}>
                {r.rate_pct}% ({r.completed}/{r.invited})
              </span>
            </div>
          ))}
        </Card>
        <Card className="p-5 shadow-card" data-testid="clsup-widget-expiring">
          <h4 className="text-sm font-bold flex items-center gap-2 mb-3">
            <CalendarClock className="h-4 w-4 text-amber-600" />
            {isES ? 'Vencen esta semana' : 'Expiring this week'}
          </h4>
          {widgets.expiring.length === 0 ? (
            <p className="text-xs text-muted-foreground">{isES ? 'Ninguna campaña vence en los próximos 7 días.' : 'No campaigns expire in the next 7 days.'}</p>
          ) : widgets.expiring.map((r) => (
            <div key={r.id} className="flex items-center justify-between text-xs py-1.5 border-b last:border-0">
              <span className="truncate mr-2">{r.company_name} · {r.name}</span>
              <span className="font-semibold shrink-0">{r.days_left === 0 ? (isES ? 'Hoy' : 'Today') : `${r.days_left} ${isES ? 'días' : 'days'}`}</span>
            </div>
          ))}
        </Card>
        <Card className="p-5 shadow-card" data-testid="clsup-widget-noresponses">
          <h4 className="text-sm font-bold flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            {isES ? 'Sin respuestas todavía' : 'No responses yet'}
          </h4>
          {widgets.noResponses.length === 0 ? (
            <p className="text-xs text-muted-foreground">{isES ? 'Todas las campañas activas tienen respuestas.' : 'All active campaigns have responses.'}</p>
          ) : widgets.noResponses.map((r) => (
            <div key={r.id} className="flex items-center justify-between text-xs py-1.5 border-b last:border-0">
              <span className="truncate mr-2">{r.company_name} · {r.name}</span>
              <span className="text-muted-foreground shrink-0">{r.invited} {isES ? 'invitados' : 'invited'}</span>
            </div>
          ))}
        </Card>
        <Card className="p-5 shadow-card" data-testid="clsup-widget-workload">
          <h4 className="text-sm font-bold flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-primary" />
            {isES ? 'Carga por asesor CTH' : 'HM Advisor workload'}
          </h4>
          {widgets.workload.length === 0 ? (
            <p className="text-xs text-muted-foreground">{isES ? 'Sin campañas registradas.' : 'No campaigns recorded.'}</p>
          ) : widgets.workload.map((w) => (
            <div key={w.advisor} className="flex items-center justify-between text-xs py-1.5 border-b last:border-0">
              <span className="truncate mr-2 font-medium">{w.advisor}</span>
              <span className="shrink-0 flex items-center gap-2 tabular-nums">
                <span>{w.companies} {isES ? 'empresas' : 'companies'}</span>
                <Badge className="bg-sky-100 text-sky-700 text-xs">{w.active} {isES ? 'activas' : 'active'}</Badge>
                {w.overdue > 0 && <Badge className="bg-rose-100 text-rose-700 text-xs">{w.overdue} {isES ? 'vencidas' : 'overdue'}</Badge>}
              </span>
            </div>
          ))}
        </Card>
      </div>

      {/* Global campaign list with filters */}
      <Card className="p-5 shadow-card" data-testid="clsup-campaign-list">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h4 className="text-sm font-bold">{isES ? 'Todas las campañas' : 'All campaigns'}</h4>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="clsup-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isES ? 'Todos los estados' : 'All statuses'}</SelectItem>
                <SelectItem value="active">{stLabel('active')}</SelectItem>
                <SelectItem value="closed">{stLabel('closed')}</SelectItem>
                <SelectItem value="expired">{stLabel('expired')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={advisorFilter} onValueChange={setAdvisorFilter}>
              <SelectTrigger className="w-48 h-8 text-xs" data-testid="clsup-advisor-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isES ? 'Todos los asesores' : 'All advisors'}</SelectItem>
                {(data.advisors || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                <SelectItem value="__none__">{isES ? 'Sin asesor asignado' : 'No advisor'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b">
              <tr>
                <th className="text-left py-2 pr-3">{isES ? 'Empresa' : 'Company'}</th>
                <th className="text-left py-2 px-3">{isES ? 'Campaña' : 'Campaign'}</th>
                <th className="text-left py-2 px-3">{isES ? 'Asesor' : 'Advisor'}</th>
                <th className="text-center py-2 px-3">{isES ? 'Participación' : 'Participation'}</th>
                <th className="text-left py-2 px-3">{isES ? 'Fecha límite' : 'Deadline'}</th>
                <th className="text-left py-2 pl-3">{isES ? 'Estado' : 'Status'}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0" data-testid={`clsup-row-${r.id}`}>
                  <td className="py-2 pr-3 font-medium">{r.company_name}</td>
                  <td className="py-2 px-3">{r.name} · {r.wave_label}</td>
                  <td className="py-2 px-3 text-muted-foreground">{r.advisor_name || '—'}</td>
                  <td className="py-2 px-3 text-center tabular-nums font-semibold">{r.invited ? `${r.rate_pct}% (${r.completed}/${r.invited})` : '—'}</td>
                  <td className="py-2 px-3">{r.deadline || '—'}</td>
                  <td className="py-2 pl-3">
                    <Badge className={`text-xs ${ST_TONE[r.derived_status] || ST_TONE.closed}`}>{stLabel(r.derived_status)}</Badge>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">
                  {isES ? 'Sin campañas para este filtro.' : 'No campaigns for this filter.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
